import { BASE_URL } from "../../../../shared/utils/variables";
import { getAuthHeaders } from "../../../../shared/services/authentication";

const baseURL = BASE_URL;

export interface BeneficiaryProjectData {
    beneficiaryId: string;
    projectId: string;
    project_end_date: string;
    monthly_cost: number;
    description?: string;
}

export interface BeneficiaryProjectUpdateData {
    project_end_date?: string;
    monthly_cost?: number;
    description?: string;
    is_active?: boolean;
}

export interface BeneficiaryProject {
    id: string;
    beneficiaryId: string;
    projectId: string;
    project_name: string;
    project_start_date: string;
    project_end_date: string;
    monthly_cost: number;
    monthly_cost_display: number;
    description?: string;
    companyId: string;
    is_active: boolean;
    created_at: string;
    beneficiary?: any;
    project?: any;
}

export interface BeneficiaryProjectResponse {
    id: string;
    beneficiaryId: string;
    projectId: string;
    project_name: string;
    project_end_date: string;
    monthly_cost: number;
    monthly_cost_display: number;
    description: string;
    is_active: boolean;
    companyId: string;
    createdAt: string;
    updatedAt: string;
}


// Função para extrair array de resposta
const extractArrayFromResponse = (data: any): any[] => {
    if (Array.isArray(data)) {
        return data;
    }
    
    if (!data) {
        return [];
    }
    
    if (typeof data === 'object') {
        
        const possibleArrayKeys = [
            'data', 'items', 'results', 'content', 'list', 
            'beneficiaryProjects', 'projects', 'beneficiaries'
        ];
        
        for (const key of possibleArrayKeys) {
            if (data[key] && Array.isArray(data[key])) {
                return data[key];
            }
        }
        
        for (const key in data) {
            if (Array.isArray(data[key])) {
                return data[key];
            }
        }
        
        return [];
    }
    
    return [];
};

export const fetchBeneficiaryProjectsService = async (): Promise<BeneficiaryProject[]> => {
    try {
        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(`${baseURL}/beneficiary-project`, options);
        
        if (!response.ok) {
            throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }
        
        const rawText = await response.text();
        
        let data;
        try {
            data = JSON.parse(rawText);
        } catch (parseError) {
            throw new Error('Resposta do servidor não é um JSON válido');
        }
        
        const arrayData = extractArrayFromResponse(data);
        
        return arrayData;
    } catch (error) {
        throw error;
    }
};

export const fetchBeneficiaryProjectService = async (id: string): Promise<BeneficiaryProject> => {
    try {
        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(`${baseURL}/beneficiary-project/${id}`, options);
        
        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('Vínculo não encontrado');
            }
            throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        return data;
    } catch (error) {
        throw error;
    }
};

export const createBeneficiaryProjectService = async (data: BeneficiaryProjectData): Promise<BeneficiaryProjectResponse> => {
    const response = await fetch(`${baseURL}/beneficiary-project`, {
        method: 'POST',
        headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao criar vínculo beneficiário-projeto');
    }

    const result = await response.json();
    return result.link; // O controller retorna { message, link }
};

export const updateBeneficiaryProjectService = async (id: string, data: BeneficiaryProjectUpdateData): Promise<any> => {
    try {
        const processedData = {
            ...data,
            ...(data.monthly_cost && { monthly_cost: Math.round(data.monthly_cost * 100) })
        };
        
        const options = {
            method: 'PUT',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
            body: JSON.stringify(processedData)
        };

        const response = await fetch(`${baseURL}/beneficiary-project/${id}`, options);
        const result = await response.json();

        if (!response.ok) {
            throw new Error(result.message || 'Erro ao atualizar vínculo');
        }

        return result;
    } catch (error) {
        throw error;
    }
};

export const deleteBeneficiaryProjectService = async (id: string): Promise<any> => {
    try {
        const options = {
            method: 'DELETE',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(`${baseURL}/beneficiary-project/${id}`, options);
        
        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('Vínculo não encontrado');
            }
            const result = await response.json();
            throw new Error(result.message || 'Erro ao deletar vínculo');
        }

        const result = await response.json();
        return result;
    } catch (error) {
        throw error;
    }
};

export const fetchMonthlyBeneficiariesService = async (year: number, month: number): Promise<any> => {
    try {
        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(`${baseURL}/beneficiary-project/monthly?year=${year}&month=${month}`, options);
        
        if (!response.ok) {
            throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        throw error;
    }
};

export const fetchProjectCostReportService = async (year: number, month: number): Promise<any> => {
    try {
        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(`${baseURL}/beneficiary-project/cost-report?year=${year}&month=${month}`, options);
        
        if (!response.ok) {
            throw new Error(`Erro ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        return data;
    } catch (error) {
        throw error;
    }
};

export const fetchBeneficiariesService = async (): Promise<any[]> => {
    try {
        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(`${baseURL}/beneficiaries`, options);
        
        if (!response.ok) {
            throw new Error(`Erro ao buscar beneficiários: ${response.status}`);
        }
        
        const data = await response.json();
        const arrayData = extractArrayFromResponse(data);
        
        return arrayData;
    } catch (error) {
        return [];
    }
};

export const fetchProjectsService = async (): Promise<any[]> => {
    try {
        const options = {
            method: 'GET',
            headers: { 
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
        };

        const response = await fetch(`${baseURL}/projects`, options);
        
        if (!response.ok) {
            throw new Error(`Erro ao buscar projetos: ${response.status}`);
        }
        
        const data = await response.json();
        const arrayData = extractArrayFromResponse(data);
        
        return arrayData;
    } catch (error) {
        return [];
    }
};